import { PropsWithChildren, ComponentType } from "react";
import {
  action,
  IObservableArray,
  makeObservable,
  observable,
  toJS,
} from "mobx";
import { GraphQLError } from "graphql";
import { IStoreElement } from "types/store";
import { ExtendedNextPageContext } from "types/app";
import { generateUniq } from "helpers/generateId";

const TOAST = "toast";
const MODAL = "modal";
const TOOLTIP = "tooltip";

export type NotificationType = typeof TOAST | typeof MODAL | typeof TOOLTIP;
export type NotificationComponentProps = PropsWithChildren<Record<string, any>>;
export type Notification = {
  id?: string;
  type: NotificationType;
  component?: ComponentType<NotificationComponentProps>;
  props?: NotificationComponentProps;
  permanent?: boolean;
};

export default class Notifications implements IStoreElement {
  private ctx?: ExtendedNextPageContext;
  @observable.shallow toasts = [] as IObservableArray<Notification>;
  @observable.shallow modals = [] as IObservableArray<Notification>;
  @observable.shallow tooltips = [] as IObservableArray<Notification>;

  constructor() {
    makeObservable(this);
  }

  toJSON() {
    return { modals: toJS(this.modals) };
  }

  @action
  show(notification: Notification): () => void {
    notification.id = generateUniq();
    switch (notification.type) {
      case TOAST:
        this.toasts.push(notification);
        break;
      case MODAL:
        this.modals.push(notification);
        break;
      case TOOLTIP:
        this.tooltips.push(notification);
        break;
      default:
        throw new Error("Notification type not valid");
    }

    return () => {
      this.close(notification);
    };
  }

  @action
  close({ type, id }: Notification): void {
    switch (type) {
      case TOAST:
        this.toasts = this.toasts.filter(
          i => i.id !== id
        ) as IObservableArray<Notification>;
        break;
      case MODAL:
        this.modals = this.modals.filter(
          i => i.id !== id
        ) as IObservableArray<Notification>;
        break;
      case TOOLTIP:
        this.tooltips = this.tooltips.filter(
          i => i.id !== id
        ) as IObservableArray<Notification>;
        break;
      default:
        throw new Error("Notification type not valid");
    }
  }

  @action
  showAPIErrors(
    errors: ReadonlyArray<GraphQLError> = [],
    title = "Ошибка"
  ): void {
    errors.forEach(err => {
      this.show({
        type: "toast",
        props: {
          title,
          children: err.message,
        },
      });
    });
  }
}
